import {
  ACCEPTED_MEDIA_TYPES,
  MAX_IMAGE_SIZE,
  MAX_VIDEO_SIZE,
} from '../constants/mediaTypes';

export type MediaType = 'image' | 'video' | 'unknown';

export function getMediaType(file: File | string | null): MediaType {
  if (!file) return 'unknown';

  if (file instanceof File) {
    if (
      file.type === 'video/mp4' ||
      file.type === 'video/x-m4v' ||
      file.type === 'application/mp4'
    ) {
      return 'video';
    }
    if (file.type.startsWith('image/')) return 'image';
    if (file.name.toLowerCase().endsWith('.mp4')) return 'video';
  } else if (typeof file === 'string') {
    const normalized = file.toLowerCase();
    const path = (() => {
      try {
        return new URL(file).pathname.toLowerCase();
      } catch {
        return normalized.split('?')[0].split('#')[0];
      }
    })();
    if (path.endsWith('.mp4')) return 'video';
    if (/\.(jpg|jpeg|png|gif|webp)$/i.test(path)) return 'image';
  }

  return 'unknown';
}

function getMaxSizeForMedia(file: File): number {
  const type = getMediaType(file);
  if (type === 'video') return MAX_VIDEO_SIZE;
  if (type === 'image') return MAX_IMAGE_SIZE;
  return 0;
}

export interface MediaFileValidationResult {
  valid: boolean;
  error?: {
    type: 'InvalidInput' | 'FileSizeExceeded';
    message: string;
  };
}

export function validateMediaFile(file: File): MediaFileValidationResult {
  const isAcceptedType =
    ACCEPTED_MEDIA_TYPES.includes(file.type) ||
    (file.type === '' && file.name.toLowerCase().endsWith('.mp4'));

  if (!isAcceptedType) {
    return {
      valid: false,
      error: {
        type: 'InvalidInput',
        message: 'Only image files (jpg, png, webp) and MP4 videos are allowed',
      },
    };
  }

  const maxSize = getMaxSizeForMedia(file);
  if (file.size > maxSize) {
    const mediaType = getMediaType(file);
    const sizeLimitMB = mediaType === 'video' ? 30 : 3;
    const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);

    return {
      valid: false,
      error: {
        type: 'FileSizeExceeded',
        message: `File size (${fileSizeMB}MB) exceeds ${sizeLimitMB}MB limit`,
      },
    };
  }

  return { valid: true };
}
